import { motion } from 'framer-motion';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HealthAssessmentQuiz from './components/HealthAssessmentQuiz';

const programs = [
  {
    id: 1,
    title: 'Heart Health Education',
    icon: 'ri-heart-pulse-line',
    color: 'coral',
    tagline: 'Protecting Your Most Vital Organ',
    description: 'Comprehensive cardiovascular wellness education focusing on prevention, early detection, and healthy lifestyle choices.',
    image: 'https://public.readdy.ai/ai/img_res/6115cfe801102a9e480ed79950833019.jpg',
    initiatives: [
      'Blood pressure screening events',
      'Nutrition and exercise workshops',
      'Heart disease risk assessment',
      'CPR and first aid training',
      'Healthy cooking demonstrations',
    ],
    stats: { served: '2,500+', events: '30+' },
    heartHealthyMeals: [
      { name: 'Grilled Salmon with Quinoa', benefit: 'Rich in omega-3 fatty acids', icon: 'ri-restaurant-line' },
      { name: 'Mediterranean Salad', benefit: 'Loaded with antioxidants', icon: 'ri-leaf-line' },
      { name: 'Oatmeal with Berries', benefit: 'Lowers cholesterol naturally', icon: 'ri-bowl-line' },
      { name: 'Grilled Chicken & Vegetables', benefit: 'Lean protein, low sodium', icon: 'ri-fire-line' },
    ],
    heartHealthyDrinks: [
      { name: 'Green Tea', benefit: 'Reduces blood pressure', icon: 'ri-cup-line' },
      { name: 'Beet Juice', benefit: 'Improves blood flow', icon: 'ri-goblet-line' },
      { name: 'Hibiscus Tea', benefit: 'Lowers cholesterol', icon: 'ri-flask-line' },
      { name: 'Pomegranate Juice', benefit: 'Powerful antioxidants', icon: 'ri-drop-line' },
    ],
    helpfulTools: [
      { name: 'Blood Pressure Monitor', description: 'Track your BP daily at home', icon: 'ri-pulse-line' },
      { name: 'Heart Rate App', description: 'Monitor your resting heart rate', icon: 'ri-smartphone-line' },
      { name: 'Food Diary', description: 'Track sodium and fat intake', icon: 'ri-book-2-line' },
      { name: 'Exercise Tracker', description: 'Log 150 min/week of activity', icon: 'ri-run-line' },
    ],
  },
  {
    id: 2,
    title: 'Mental Health Support',
    icon: 'ri-mental-health-line',
    color: 'purple',
    tagline: 'Breaking Stigma, Building Wellness',
    description: 'Creating safe spaces for mental health conversations while providing resources for emotional wellness and professional support.',
    image: 'https://readdy.ai/api/search-image?query=peaceful%20meditation%20and%20mental%20wellness%20concept%20with%20person%20sitting%20in%20serene%20natural%20environment%20practicing%20mindfulness%20surrounded%20by%20calming%20nature%20elements%20soft%20purple%20and%20lavender%20tones%20representing%20inner%20peace%20emotional%20balance%20mental%20health%20awareness%20and%20self%20care%20with%20gentle%20natural%20lighting%20tranquil%20atmosphere&width=800&height=600&seq=mental001&orientation=landscape',
    initiatives: [
      'Mental health awareness workshops',
      'Stress management techniques',
      'Support group facilitation',
      'Crisis resource connections',
      'Mindfulness and meditation sessions',
    ],
    stats: { served: '1,800+', events: '25+' },
    heartHealthyMeals: [
      { name: 'Salmon & Leafy Greens', benefit: 'Omega-3s boost mood', icon: 'ri-restaurant-line' },
      { name: 'Dark Chocolate & Nuts', benefit: 'Releases feel-good endorphins', icon: 'ri-cake-3-line' },
      { name: 'Avocado Toast', benefit: 'Healthy fats for brain health', icon: 'ri-bread-line' },
      { name: 'Turkey & Whole Grains', benefit: 'Tryptophan promotes calm', icon: 'ri-bowl-line' },
    ],
    heartHealthyDrinks: [
      { name: 'Chamomile Tea', benefit: 'Natural calming effect', icon: 'ri-cup-line' },
      { name: 'Warm Turmeric Milk', benefit: 'Anti-inflammatory benefits', icon: 'ri-goblet-line' },
      { name: 'Lavender Lemonade', benefit: 'Reduces anxiety naturally', icon: 'ri-flask-line' },
      { name: 'Matcha Green Tea', benefit: 'L-theanine for focus', icon: 'ri-drop-line' },
    ],
    helpfulTools: [
      { name: 'Meditation Apps', description: 'Calm, Headspace, Insight Timer', icon: 'ri-mental-health-line' },
      { name: 'Mood Journal', description: 'Track emotions and triggers', icon: 'ri-book-2-line' },
      { name: 'Crisis Hotline', description: '988 Suicide & Crisis Lifeline', icon: 'ri-phone-line' },
      { name: 'Sleep Tracker', description: 'Monitor sleep quality', icon: 'ri-moon-line' },
    ],
  },
  {
    id: 3,
    title: 'Breast Cancer Awareness',
    icon: 'ri-awareness-line',
    color: 'pink',
    tagline: 'Early Detection Saves Lives',
    description: 'Empowering women with knowledge about breast health, self-examination techniques, and access to screening resources.',
    image: 'https://readdy.ai/api/search-image?query=breast%20cancer%20awareness%20concept%20with%20pink%20ribbon%20symbol%20supportive%20hands%20forming%20heart%20shape%20representing%20hope%20strength%20and%20community%20support%20with%20soft%20pink%20tones%20clean%20white%20background%20empowering%20message%20of%20early%20detection%20survivor%20solidarity%20and%20women%20health%20in%20professional%20healthcare%20photography%20style&width=800&height=600&seq=breast001&orientation=landscape',
    initiatives: [
      'Free mammography screening events',
      'Self-examination education',
      'Survivor support networks',
      'Risk factor awareness campaigns',
      'Family history counseling',
    ],
    stats: { served: '1,200+', events: '20+' },
    heartHealthyMeals: [
      { name: 'Cruciferous Vegetables', benefit: 'Broccoli, kale fight cancer cells', icon: 'ri-leaf-line' },
      { name: 'Berries & Citrus Fruits', benefit: 'High in antioxidants', icon: 'ri-restaurant-line' },
      { name: 'Garlic & Onion Dishes', benefit: 'Natural cancer fighters', icon: 'ri-fire-line' },
      { name: 'Legumes & Beans', benefit: 'Fiber reduces cancer risk', icon: 'ri-bowl-line' },
    ],
    heartHealthyDrinks: [
      { name: 'Green Tea', benefit: 'EGCG fights cancer cells', icon: 'ri-cup-line' },
      { name: 'Carrot Ginger Juice', benefit: 'Beta-carotene protection', icon: 'ri-goblet-line' },
      { name: 'Tomato Juice', benefit: 'Lycopene antioxidants', icon: 'ri-flask-line' },
      { name: 'Aloe Vera Water', benefit: 'Immune system support', icon: 'ri-drop-line' },
    ],
    helpfulTools: [
      { name: 'Self-Exam Guide', description: 'Monthly breast self-check', icon: 'ri-hand-heart-line' },
      { name: 'Mammogram Reminder', description: 'Schedule annual screenings', icon: 'ri-calendar-check-line' },
      { name: 'Symptom Tracker', description: 'Note any breast changes', icon: 'ri-file-list-3-line' },
      { name: 'Support Groups', description: 'Connect with survivors', icon: 'ri-team-line' },
    ],
  },
  {
    id: 4,
    title: 'Domestic Violence Prevention',
    icon: 'ri-shield-check-line',
    color: 'teal',
    tagline: 'Creating Safe Spaces for Healing',
    description: 'Providing education, resources, and support for survivors while working to prevent domestic violence through community awareness.',
    image: 'https://readdy.ai/api/search-image?query=protective%20supportive%20hands%20creating%20safe%20space%20with%20teal%20and%20turquoise%20color%20theme%20representing%20domestic%20violence%20prevention%20safety%20support%20and%20empowerment%20with%20symbols%20of%20strength%20hope%20community%20protection%20and%20healing%20in%20compassionate%20respectful%20visual%20style%20with%20clean%20background&width=800&height=600&seq=dv001&orientation=landscape',
    initiatives: [
      'Safety planning workshops',
      'Legal resource connections',
      'Emergency shelter partnerships',
      'Empowerment and healing programs',
      'Community awareness campaigns',
    ],
    stats: { served: '800+', events: '15+' },
    heartHealthyMeals: [
      { name: 'Comfort Soups', benefit: 'Warm, nourishing, healing', icon: 'ri-bowl-line' },
      { name: 'Whole Grain Pasta', benefit: 'Sustained energy for recovery', icon: 'ri-restaurant-line' },
      { name: 'Fresh Fruit Bowls', benefit: 'Vitamins for stress recovery', icon: 'ri-leaf-line' },
      { name: 'Lean Protein Meals', benefit: 'Rebuilds strength', icon: 'ri-fire-line' },
    ],
    heartHealthyDrinks: [
      { name: 'Warm Honey Lemon', benefit: 'Soothing and calming', icon: 'ri-cup-line' },
      { name: 'Peppermint Tea', benefit: 'Eases tension headaches', icon: 'ri-goblet-line' },
      { name: 'Coconut Water', benefit: 'Hydration and electrolytes', icon: 'ri-flask-line' },
      { name: 'Ginger Tea', benefit: 'Reduces stress nausea', icon: 'ri-drop-line' },
    ],
    helpfulTools: [
      { name: 'Safety Plan Template', description: 'Create your escape plan', icon: 'ri-shield-check-line' },
      { name: 'Hotline: 1-800-799-7233', description: 'National DV Hotline 24/7', icon: 'ri-phone-line' },
      { name: 'Document Storage', description: 'Secure important papers', icon: 'ri-folder-shield-line' },
      { name: 'Local Shelter Finder', description: 'Find safe housing nearby', icon: 'ri-home-heart-line' },
    ],
  },
  {
    id: 5,
    title: 'HIV Awareness & Prevention',
    icon: 'ri-first-aid-kit-line',
    color: 'blue',
    tagline: 'Knowledge, Testing, Support',
    description: 'Reducing stigma while providing education about HIV prevention, testing resources, and support for affected individuals.',
    image: 'https://readdy.ai/api/search-image?query=HIV%20awareness%20and%20prevention%20concept%20with%20red%20ribbon%20symbol%20medical%20testing%20equipment%20and%20supportive%20diverse%20community%20imagery%20in%20blue%20tones%20representing%20education%20prevention%20compassionate%20healthcare%20and%20stigma%20free%20support%20with%20professional%20medical%20photography%20style%20clean%20background&width=800&height=600&seq=hiv001&orientation=landscape',
    initiatives: [
      'Free HIV testing events',
      'Prevention education workshops',
      'PrEP and PEP information',
      'Stigma reduction campaigns',
      'Support group connections',
    ],
    stats: { served: '1,000+', events: '18+' },
    heartHealthyMeals: [
      { name: 'Protein-Rich Foods', benefit: 'Maintains muscle mass', icon: 'ri-restaurant-line' },
      { name: 'Leafy Green Salads', benefit: 'Boosts immune function', icon: 'ri-leaf-line' },
      { name: 'Whole Grains & Fiber', benefit: 'Supports gut health', icon: 'ri-bowl-line' },
      { name: 'Lean Meats & Fish', benefit: 'Essential nutrients', icon: 'ri-fire-line' },
    ],
    heartHealthyDrinks: [
      { name: 'Fortified Orange Juice', benefit: 'Vitamin C immunity boost', icon: 'ri-cup-line' },
      { name: 'Protein Smoothies', benefit: 'Nutrient-dense calories', icon: 'ri-goblet-line' },
      { name: 'Bone Broth', benefit: 'Gut healing properties', icon: 'ri-flask-line' },
      { name: 'Electrolyte Drinks', benefit: 'Maintains hydration', icon: 'ri-drop-line' },
    ],
    helpfulTools: [
      { name: 'HIV Testing Locator', description: 'Find free testing near you', icon: 'ri-map-pin-line' },
      { name: 'PrEP Information', description: 'Prevention medication guide', icon: 'ri-capsule-line' },
      { name: 'Medication Reminder', description: 'Stay on treatment schedule', icon: 'ri-alarm-line' },
      { name: 'Support Network', description: 'Connect with community', icon: 'ri-team-line' },
    ],
  },
];

const strokeSigns = [
  { letter: 'F', word: 'Face', description: 'Does one side of the face droop? Ask the person to smile.', icon: 'ri-emotion-line' },
  { letter: 'A', word: 'Arms', description: 'Is one arm weak or numb? Ask the person to raise both arms.', icon: 'ri-hand-heart-line' },
  { letter: 'S', word: 'Speech', description: 'Is speech slurred? Ask the person to repeat a simple sentence.', icon: 'ri-chat-voice-line' },
  { letter: 'T', word: 'Time', description: 'Time to call 911! Note the time symptoms first appeared.', icon: 'ri-time-line' },
];

const additionalStrokeSigns = [
  'Sudden numbness or weakness in leg',
  'Sudden confusion or trouble understanding',
  'Sudden trouble seeing in one or both eyes',
  'Sudden trouble walking, dizziness, loss of balance',
  'Sudden severe headache with no known cause',
];

export default function ProgramsPage() {
  const [expandedProgram, setExpandedProgram] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-cream to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-charcoal mb-6">
              Our Programs & Initiatives
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Comprehensive health education and support across five critical areas of community wellness. Each program is designed to empower, educate, and create lasting positive change.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAST Stroke Warning Signs */}
      <section className="py-16 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
            </div>
            <h2 className="font-serif text-4xl font-bold text-charcoal mb-4">
              Know the Signs of a Stroke
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              As a two-time stroke survivor, LaTonya knows how critical it is to recognize the warning signs. Remember <span className="font-bold text-red-600">F.A.S.T.</span> — it could save a life!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 mb-10">
            {strokeSigns.map((sign, index) => (
              <motion.div
                key={sign.letter}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-center"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-red-600 text-white rounded-full mx-auto mb-4">
                  <span className="text-4xl font-bold">{sign.letter}</span>
                </div>
                <h3 className="text-xl font-bold text-charcoal mb-2">{sign.word}</h3>
                <p className="text-gray-600 text-sm">{sign.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h3 className="font-bold text-lg text-charcoal mb-4 flex items-center">
              <i className="ri-error-warning-line text-red-600 mr-2"></i>
              Additional Warning Signs to Watch For:
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {additionalStrokeSigns.map((sign, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-0.5"></i>
                  <span className="text-gray-700 text-sm">{sign}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-red-700 font-semibold text-center">
                <i className="ri-phone-line mr-2"></i>
                If you notice ANY of these signs, call 911 immediately. Every minute counts!
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Health Assessment Quiz */}
      <HealthAssessmentQuiz />

      {/* Programs Detail */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {programs.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}>
                  {/* Image */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                      <img
                        src={program.image}
                        alt={program.title}
                        className="w-full h-96 object-cover object-top"
                      />
                      <div className={`absolute inset-0 bg-${program.color}/10`}></div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className={`w-16 h-16 flex items-center justify-center bg-${program.color}/10 rounded-2xl mb-6`}>
                      <i className={`${program.icon} text-4xl text-${program.color}`}></i>
                    </div>
                    <h2 className="font-serif text-4xl font-bold text-charcoal mb-3">
                      {program.title}
                    </h2>
                    <p className={`text-${program.color} font-semibold text-lg mb-4`}>
                      {program.tagline}
                    </p>
                    <p className="text-gray-700 text-base leading-relaxed mb-6">
                      {program.description}
                    </p>

                    {/* Initiatives */}
                    <div className="mb-6">
                      <h3 className="font-bold text-lg text-charcoal mb-3">Key Initiatives:</h3>
                      <ul className="space-y-2">
                        {program.initiatives.map((initiative, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <i className={`ri-checkbox-circle-fill text-${program.color} mt-1`}></i>
                            <span className="text-gray-700 text-sm">{initiative}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-6 mb-6">
                      <div className={`bg-${program.color}/10 rounded-xl px-6 py-4`}>
                        <div className={`text-3xl font-bold text-${program.color} mb-1`}>
                          {program.stats.served}
                        </div>
                        <div className="text-gray-600 text-sm">People Served</div>
                      </div>
                      <div className={`bg-${program.color}/10 rounded-xl px-6 py-4`}>
                        <div className={`text-3xl font-bold text-${program.color} mb-1`}>
                          {program.stats.events}
                        </div>
                        <div className="text-gray-600 text-sm">Events Held</div>
                      </div>
                    </div>

                    {/* Expand Button */}
                    <button
                      onClick={() => setExpandedProgram(expandedProgram === program.id ? null : program.id)}
                      className={`flex items-center space-x-2 text-${program.color} font-semibold hover:underline cursor-pointer`}
                    >
                      <span>{expandedProgram === program.id ? 'Hide Resources' : 'View Healthy Resources & Tools'}</span>
                      <i className={`ri-arrow-${expandedProgram === program.id ? 'up' : 'down'}-s-line`}></i>
                    </button>
                  </div>
                </div>

                {/* Expanded Resources Section */}
                {expandedProgram === program.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-12"
                  >
                    <div className="grid md:grid-cols-3 gap-8">
                      {/* Heart Healthy Meals */}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 shadow-lg">
                        <div className="flex items-center space-x-3 mb-5">
                          <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-xl">
                            <i className="ri-restaurant-2-line text-2xl text-white"></i>
                          </div>
                          <h3 className="font-bold text-lg text-charcoal">Heart Healthy Meals</h3>
                        </div>
                        <div className="space-y-4">
                          {program.heartHealthyMeals.map((meal, idx) => (
                            <div key={idx} className="flex items-start space-x-3 bg-white rounded-xl p-3 shadow-sm">
                              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                                <i className={`${meal.icon} text-green-600`}></i>
                              </div>
                              <div>
                                <p className="font-semibold text-charcoal text-sm">{meal.name}</p>
                                <p className="text-gray-500 text-xs">{meal.benefit}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Heart Healthy Drinks */}
                      <div className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-2xl p-6 shadow-lg">
                        <div className="flex items-center space-x-3 mb-5">
                          <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 rounded-xl">
                            <i className="ri-goblet-line text-2xl text-white"></i>
                          </div>
                          <h3 className="font-bold text-lg text-charcoal">Heart Healthy Drinks</h3>
                        </div>
                        <div className="space-y-4">
                          {program.heartHealthyDrinks.map((drink, idx) => (
                            <div key={idx} className="flex items-start space-x-3 bg-white rounded-xl p-3 shadow-sm">
                              <div className="w-10 h-10 flex items-center justify-center bg-cyan-100 rounded-lg flex-shrink-0">
                                <i className={`${drink.icon} text-cyan-600`}></i>
                              </div>
                              <div>
                                <p className="font-semibold text-charcoal text-sm">{drink.name}</p>
                                <p className="text-gray-500 text-xs">{drink.benefit}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Helpful Tools */}
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 shadow-lg">
                        <div className="flex items-center space-x-3 mb-5">
                          <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl">
                            <i className="ri-tools-line text-2xl text-white"></i>
                          </div>
                          <h3 className="font-bold text-lg text-charcoal">Helpful Tools</h3>
                        </div>
                        <div className="space-y-4">
                          {program.helpfulTools.map((tool, idx) => (
                            <div key={idx} className="flex items-start space-x-3 bg-white rounded-xl p-3 shadow-sm">
                              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                                <i className={`${tool.icon} text-amber-600`}></i>
                              </div>
                              <div>
                                <p className="font-semibold text-charcoal text-sm">{tool.name}</p>
                                <p className="text-gray-500 text-xs">{tool.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Reference Card */}
      <section className="py-16 bg-gradient-to-br from-coral/10 to-pink/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl font-bold text-charcoal mb-4">
              Quick Health Reference
            </h2>
            <p className="text-lg text-gray-600">
              Essential tips for maintaining heart health and overall wellness
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                <i className="ri-heart-pulse-line text-3xl text-red-500"></i>
              </div>
              <h3 className="font-bold text-charcoal mb-2">Blood Pressure</h3>
              <p className="text-2xl font-bold text-red-500 mb-1">120/80</p>
              <p className="text-gray-500 text-sm">Ideal reading</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-run-line text-3xl text-green-500"></i>
              </div>
              <h3 className="font-bold text-charcoal mb-2">Weekly Exercise</h3>
              <p className="text-2xl font-bold text-green-500 mb-1">150 min</p>
              <p className="text-gray-500 text-sm">Moderate activity</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-cyan-100 rounded-full mx-auto mb-4">
                <i className="ri-drop-line text-3xl text-cyan-500"></i>
              </div>
              <h3 className="font-bold text-charcoal mb-2">Daily Water</h3>
              <p className="text-2xl font-bold text-cyan-500 mb-1">8 glasses</p>
              <p className="text-gray-500 text-sm">Stay hydrated</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-lg text-center"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                <i className="ri-moon-line text-3xl text-purple-500"></i>
              </div>
              <h3 className="font-bold text-charcoal mb-2">Nightly Sleep</h3>
              <p className="text-2xl font-bold text-purple-500 mb-1">7-9 hrs</p>
              <p className="text-gray-500 text-sm">Quality rest</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Books & Design Section */}
      <section id="books-design" className="py-20 bg-gradient-to-br from-purple-50 via-pink-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-purple-600/10 rounded-2xl mx-auto mb-6">
              <i className="ri-book-open-line text-4xl text-purple-600"></i>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Books & Design
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Inspiring publications and creative design services that empower, educate, and transform lives through the power of storytelling and visual communication.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                image: 'https://readdy.ai/api/search-image?query=professional%20book%20cover%20design%20about%20heart%20health%20and%20wellness%20with%20elegant%20typography%20modern%20minimalist%20style%20soft%20coral%20and%20teal%20colors%20clean%20background&width=400&height=600&seq=book1&orientation=portrait',
                title: 'Health & Wellness Publications',
                description: 'Comprehensive guides on heart health, nutrition, and preventive care written by our expert health educators.'
              },
              {
                image: 'https://readdy.ai/api/search-image?query=inspirational%20book%20cover%20design%20about%20storytelling%20and%20personal%20growth%20with%20elegant%20typography%20modern%20style%20purple%20and%20gold%20accents%20clean%20background&width=400&height=600&seq=book2&orientation=portrait',
                title: 'Inspirational Stories',
                description: 'Powerful narratives of transformation, resilience, and community impact from The Story Coach series.'
              },
              {
                image: 'https://readdy.ai/api/search-image?query=wellness%20book%20cover%20design%20about%20holistic%20health%20and%20mindfulness%20with%20elegant%20typography%20modern%20style%20teal%20and%20sage%20green%20colors%20clean%20background&width=400&height=600&seq=book3&orientation=portrait',
                title: 'Holistic Wellness Guides',
                description: 'Practical resources for alignment, restoration, and mindful living from our wellness experts.'
              }
            ].map((book, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="relative h-80 bg-gray-100">
                  <img
                    src={book.image}
                    alt={book.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl font-bold text-charcoal mb-3">
                    {book.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {book.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="/books"
              className="inline-flex items-center bg-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Explore Our Books & Design Services
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-coral to-pink">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Get Involved?
            </h2>
            <p className="text-xl text-white/95 mb-8 leading-relaxed">
              Join us in making a difference. Whether you want to volunteer, attend an event, or support our mission, there's a place for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-white text-coral px-8 py-4 rounded-full text-base font-semibold hover:bg-white/95 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Volunteer With Us
              </a>
              <a
                href="/events"
                className="bg-charcoal text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-charcoal/90 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                View Upcoming Events
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
